import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FiBell, FiSend, FiUsers, FiClock, FiImage, FiX,
  FiCheckSquare, FiSquare, FiSearch, FiRefreshCw,
  FiRadio, FiTarget, FiEye, FiZap, FiChevronRight,
  FiAlertCircle, FiCheck,
} from "react-icons/fi";

const API = "https://estatebackend.pixelmindsolutions.com/api";
const getToken = () => sessionStorage.getItem("adminToken") || "";

const TABS = { BROADCAST: "broadcast", TARGETED: "targeted", HISTORY: "history" };

/* ─── Image Preview Modal ─── */
const ImagePreviewModal = ({ src, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={onClose}>
      <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt="Preview" className="w-full rounded-3xl shadow-2xl object-contain max-h-[80vh]" />
        <button onClick={onClose} className="absolute -top-3 -right-3 w-9 h-9 bg-white text-gray-800 rounded-full flex items-center justify-center shadow-xl hover:bg-gray-100 transition-all hover:scale-110">
          <FiX size={16} />
        </button>
      </div>
    </div>,
    document.body
  );
};

/* ─── Stat Pill ─── */
const StatPill = ({ icon, label, value, cls }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${cls}`}>
    {icon}
    <span className="opacity-70">{label}:</span>
    <span className="font-bold">{value}</span>
  </div>
);

/* ─── Image Upload ─── */
const ImageUpload = ({ imagePreview, image, onUpload, onRemove, onPreview, fileRef }) => (
  <div>
    <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-red-300 mb-2">
      Image <span className="normal-case font-normal text-gray-400">(optional)</span>
    </label>
    {imagePreview ? (
      <div className="relative w-full h-36 rounded-2xl overflow-hidden border border-red-100 group shadow-sm">
        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
          <button onClick={() => onPreview(imagePreview)} className="w-9 h-9 bg-white/90 text-gray-800 rounded-full flex items-center justify-center hover:bg-white transition-all hover:scale-105 shadow">
            <FiEye size={14} />
          </button>
          <button onClick={onRemove} className="w-9 h-9 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all hover:scale-105 shadow">
            <FiX size={14} />
          </button>
        </div>
        <span className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded-full">{image?.name}</span>
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center w-full h-28 rounded-2xl border-2 border-dashed border-red-100 cursor-pointer transition-all hover:border-red-300 hover:bg-red-50/60 group">
        <FiImage size={20} className="text-red-200 group-hover:text-red-400 transition-colors mb-1.5" />
        <span className="text-xs text-gray-400 group-hover:text-red-500 transition-colors">Click to upload image</span>
        <input ref={fileRef} type="file" hidden accept="image/*" onChange={(e) => onUpload(e.target.files[0])} />
      </label>
    )}
  </div>
);

/* ─── Field ─── */
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold tracking-[0.15em] uppercase text-red-300 mb-2">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full rounded-xl px-4 py-2.5 text-sm border border-red-100 bg-red-50/30 text-gray-800 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all placeholder:text-gray-300";

/* ─── User Row ─── */
const UserRow = ({ user, selected, onToggle }) => {
  const initial = (user.name || user.mobile || "U").charAt(0).toUpperCase();
  return (
    <div
      onClick={() => onToggle(user._id)}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all border ${selected ? "border-red-300 bg-red-50" : "border-transparent bg-white hover:bg-red-50/50 hover:border-red-100"
        }`}
    >
      <div className={`flex-shrink-0 ${selected ? "text-red-500" : "text-gray-300"}`}>
        {selected ? <FiCheckSquare size={15} /> : <FiSquare size={15} />}
      </div>
      {user.profileImage ? (
        <img src={user.profileImage} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-red-100" onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-br from-red-400 to-red-600">
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold truncate ${selected ? "text-red-800" : "text-gray-700"}`}>{user.name || "—"}</p>
        <p className="text-[11px] text-gray-400 truncate">{user.mobile || user.email || "—"}</p>
      </div>
      {user.fcmToken && <span className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0" title="FCM Active" />}
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const AdminBroadcast = () => {
  const [activeTab, setActiveTab] = useState(TABS.BROADCAST);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 5;
  const fileRef = useRef(null);
  const bcastFileRef = useRef(null);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setUsers(res.data.users || []);
    } catch (err) { console.error(err); }
    finally { setUsersLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await axios.get(`${API}/admin/broadcast/history`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setHistory(res.data.broadcasts || []);
      setHistoryPage(1);
    } catch (err) { console.error(err); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { if (activeTab === TABS.HISTORY) fetchHistory(); }, [activeTab]);

  const handleImage = (file) => {
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };
  const removeImage = () => {
    setImage(null); setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
    if (bcastFileRef.current) bcastFileRef.current.value = "";
  };
  const resetForm = () => { setTitle(""); setMessage(""); removeImage(); setSelectedUsers([]); setSendResult(null); };

  const fcmUsers = users.filter((u) => u.fcmToken);
  const filteredUsers = users.filter((u) =>
    !userSearch ||
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.mobile?.includes(userSearch) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const toggleUser = (id) => setSelectedUsers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = () => {
    const visible = filteredUsers.map((u) => u._id);
    const allSel = visible.every((id) => selectedUsers.includes(id));
    if (allSel) setSelectedUsers((prev) => prev.filter((id) => !visible.includes(id)));
    else setSelectedUsers((prev) => [...new Set([...prev, ...visible])]);
  };

  const sendBroadcast = async () => {
    if (!title.trim() || !message.trim()) { alert("Title and message are required"); return; }
    try {
      setSendLoading(true); setSendResult(null);
      const fd = new FormData();
      fd.append("title", title); fd.append("message", message);
      if (image) fd.append("image", image);
      const res = await axios.post(`${API}/admin/broadcast/send`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
      });
      setSendResult({ success: true, count: res.data.data?.notificationsCount || 0 });
      resetForm();
    } catch { setSendResult({ success: false }); }
    finally { setSendLoading(false); }
  };

  const sendTargeted = async () => {
    if (!title.trim() || !message.trim()) { alert("Title and message are required"); return; }
    if (selectedUsers.length === 0) { alert("Select at least one user"); return; }
    try {
      setSendLoading(true); setSendResult(null);
      const fd = new FormData();
      fd.append("title", title); fd.append("message", message);
      selectedUsers.forEach((id) => fd.append("userIds[]", id));
      if (image) fd.append("image", image);
      const res = await axios.post(`${API}/admin/broadcast/targeted`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
      });
      setSendResult({ success: true, count: res.data.data?.notificationsCount || 0 });
      resetForm();
    } catch { setSendResult({ success: false }); }
    finally { setSendLoading(false); }
  };

  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedUsers.includes(u._id));

  /* ── History pagination ── */
  const historyTotalPages = Math.ceil(history.length / HISTORY_PER_PAGE);
  const pagedHistory = history.slice((historyPage - 1) * HISTORY_PER_PAGE, historyPage * HISTORY_PER_PAGE);
  const historyPageNums = Array.from({ length: historyTotalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === historyTotalPages || Math.abs(p - historyPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);
                                    
  const TAB_CONFIG = [
    { key: TABS.BROADCAST, icon: <FiRadio size={14} />, label: "Broadcast" },
    { key: TABS.TARGETED, icon: <FiTarget size={14} />, label: "Targeted" },
    { key: TABS.HISTORY, icon: <FiClock size={14} />, label: "History" },
  ];

  const SendBtn = ({ onClick, disabled, icon, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold text-sm transition-all shadow-lg shadow-red-200 hover:shadow-red-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 flex items-center justify-center gap-2.5"
    >
      {sendLoading
        ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending…</>
        : <>{icon} {label}</>
      }
    </button>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        .font-display { font-family: 'Playfair Display', Georgia, serif; }
        .ab-root { font-family: 'DM Sans', sans-serif; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeUp    { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        .anim-slide-down { animation: slideDown 0.28s ease; }
        .anim-fade-up    { animation: fadeUp 0.32s ease; }
        .hcard { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .hcard:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(239,68,68,0.09); }
      `}</style>

      <div className="ab-root min-h-screen bg-[#fdf7f7] px-3 sm:px-6 py-6 sm:py-10">
        <div className="max-w-5xl mx-auto space-y-5">

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-red-400 mb-2">Admin · Notifications</div>
              <h1 className="font-display text-2xl sm:text-[2rem] font-bold text-gray-900 leading-tight">Notification Center</h1>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <StatPill icon={<FiUsers size={11} />} label="Users" value={users.length} cls="bg-red-50 border-red-100 text-red-600" />
                <StatPill icon={<FiZap size={11} />} label="FCM Ready" value={fcmUsers.length} cls="bg-emerald-50 border-emerald-100 text-emerald-600" />
              </div>
            </div>
            <button
              onClick={fetchUsers}
              className="self-start flex items-center gap-2 text-xs px-4 py-2 rounded-xl bg-white border border-red-100 text-red-400 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-all shadow-sm"
            >
              <FiRefreshCw size={12} /> Refresh Users
            </button>
          </div>

          {/* ── Result Banner ── */}
          {sendResult && (
            <div className={`anim-slide-down flex items-center justify-between gap-3 px-5 py-3.5 rounded-2xl border text-sm font-medium shadow-sm ${sendResult.success ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-300 text-red-600"
              }`}>
              <div className="flex items-center gap-2.5">
                {sendResult.success ? <FiCheck size={16} className="text-emerald-500" /> : <FiAlertCircle size={16} className="text-red-500" />}
                {sendResult.success
                  ? `Notification dispatched to ${sendResult.count} user${sendResult.count !== 1 ? "s" : ""} successfully.`
                  : "Failed to send notification. Please try again."}
              </div>
              <button onClick={() => setSendResult(null)} className="opacity-60 hover:opacity-100 transition-opacity"><FiX size={15} /></button>
            </div>
          )}

          {/* ── Main Card ── */}
          <div className="bg-white rounded-3xl shadow-sm border border-red-50 overflow-hidden">
            <div className="h-[3px] bg-gradient-to-r from-red-500 to-red-600" />

            {/* Tab Bar */}
            <div className="flex border-b border-red-50 bg-[#fffafa]">
              {TAB_CONFIG.map(({ key, icon, label }) => (
                <button
                  key={key}
                  onClick={() => { setActiveTab(key); setSendResult(null); }}
                  className={`relative flex items-center gap-2 px-5 sm:px-7 py-4 text-sm font-semibold transition-all flex-1 sm:flex-none justify-center sm:justify-start ${activeTab === key ? "text-red-600" : "text-gray-400 hover:text-red-400"
                    }`}
                >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden text-xs">{label}</span>
                  {activeTab === key && (
                    <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-gradient-to-r from-red-500 to-red-600" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-4 sm:p-6">

              {/* ── BROADCAST ── */}
              {activeTab === TABS.BROADCAST && (
                <div className="anim-fade-up grid grid-cols-1 lg:grid-cols-5 gap-5">
                  <div className="lg:col-span-3 space-y-4">
                    <div className="pb-3 border-b border-red-50">
                      <h2 className="font-display text-base font-semibold text-gray-900">Send to All Users</h2>
                      <p className="text-xs text-gray-400 mt-1">Reaches all <strong className="text-gray-600">{users.length}</strong> registered users instantly.</p>
                    </div>
                    <Field label="Title *">
                      <input className={inputCls} placeholder="e.g. New Feature Available!" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </Field>
                    <Field label="Message *">
                      <textarea rows={4} className={`${inputCls} resize-none`} placeholder="Write your notification message…" value={message} onChange={(e) => setMessage(e.target.value)} />
                    </Field>
                    <ImageUpload imagePreview={imagePreview} image={image} onUpload={handleImage} onRemove={removeImage} onPreview={setPreviewImg} fileRef={bcastFileRef} />
                    <SendBtn onClick={sendBroadcast} disabled={sendLoading} icon={<FiSend size={14} />} label="Broadcast to All Users" />
                  </div>

                  {/* FCM sidebar */}
                  <div className="lg:col-span-2">
                    <div className="bg-red-50/40 rounded-2xl border border-red-100 p-4 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[10px] font-bold tracking-[0.15em] uppercase text-red-300">FCM-Ready Users</h3>
                        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-red-100 text-red-600">{fcmUsers.length}</span>
                      </div>
                      {usersLoading ? (
                        <div className="flex-1 flex items-center justify-center py-10">
                          <div className="w-6 h-6 border-[3px] border-red-100 border-t-red-500 rounded-full animate-spin" />
                        </div>
                      ) : fcmUsers.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                          <FiZap size={28} className="text-red-100 mb-2" />
                          <p className="text-xs text-gray-400">No FCM-enabled users</p>
                        </div>
                      ) : (
                        <div className="overflow-y-auto max-h-72 space-y-1.5 flex-1">
                          {fcmUsers.map((u) => (
                            <div key={u._id} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white border border-red-50">
                              {u.profileImage ? (
                                <img src={u.profileImage} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                  {(u.name || u.mobile || "U").charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-700 truncate">{u.name || "—"}</p>
                                <p className="text-[10px] text-gray-400 truncate">{u.mobile}</p>
                              </div>
                              <span className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0" title="FCM Active" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── TARGETED ── */}
              {activeTab === TABS.TARGETED && (
                <div className="anim-fade-up grid grid-cols-1 lg:grid-cols-5 gap-5">
                  <div className="lg:col-span-3 space-y-4">
                    <div className="pb-3 border-b border-red-50">
                      <h2 className="font-display text-base font-semibold text-gray-900">Targeted Notification</h2>
                      <p className="text-xs text-gray-400 mt-1">Choose specific users from the list.</p>
                    </div>
                    {selectedUsers.length > 0 && (
                      <div className="anim-slide-down flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
                        <FiCheckSquare size={14} />
                        {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""} selected
                        <button onClick={() => setSelectedUsers([])} className="ml-auto opacity-60 hover:opacity-100 transition-opacity"><FiX size={13} /></button>
                      </div>
                    )}
                    <Field label="Title *">
                      <input className={inputCls} placeholder="e.g. Special Offer Just for You!" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </Field>
                    <Field label="Message *">
                      <textarea rows={4} className={`${inputCls} resize-none`} placeholder="Write your notification message…" value={message} onChange={(e) => setMessage(e.target.value)} />
                    </Field>
                    <ImageUpload imagePreview={imagePreview} image={image} onUpload={handleImage} onRemove={removeImage} onPreview={setPreviewImg} fileRef={fileRef} />
                    <SendBtn onClick={sendTargeted} disabled={sendLoading || selectedUsers.length === 0} icon={<FiTarget size={14} />} label={`Send to ${selectedUsers.length || 0} User${selectedUsers.length !== 1 ? "s" : ""}`} />
                  </div>

                  {/* User Picker */}
                  <div className="lg:col-span-2">
                    <div className="bg-red-50/40 rounded-2xl border border-red-100 p-4 flex flex-col h-full">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[10px] font-bold tracking-[0.15em] uppercase text-red-300">Select Users</h3>
                        <button onClick={toggleAll} className="text-[11px] px-2.5 py-1 rounded-lg border border-red-100 bg-white text-gray-500 hover:border-red-300 hover:text-red-600 transition-all font-medium">
                          {allVisibleSelected ? "Deselect All" : "Select All"}
                        </button>
                      </div>
                      <div className="relative mb-3">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-red-200" size={13} />
                        <input
                          className="w-full pl-9 pr-4 py-2 rounded-xl border border-red-100 bg-white text-xs text-gray-700 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300 transition-all placeholder:text-gray-300"
                          placeholder="Search name, mobile, email…"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />
                      </div>
                      {usersLoading ? (
                        <div className="flex-1 flex items-center justify-center py-10">
                          <div className="w-6 h-6 border-[3px] border-red-100 border-t-red-500 rounded-full animate-spin" />
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                          <FiUsers size={28} className="text-red-100 mb-2" />
                          <p className="text-xs text-gray-400">No users found</p>
                        </div>
                      ) : (
                        <div className="overflow-y-auto max-h-72 flex-1 space-y-1">
                          {filteredUsers.map((u) => (
                            <UserRow key={u._id} user={u} selected={selectedUsers.includes(u._id)} onToggle={toggleUser} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── HISTORY ── */}
              {activeTab === TABS.HISTORY && (
                <div className="anim-fade-up">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="font-display text-base font-semibold text-gray-900">Broadcast History</h2>
                      <p className="text-xs text-gray-400 mt-0.5">All sent notifications</p>
                    </div>
                    <button onClick={fetchHistory} className="flex items-center gap-2 text-xs px-3.5 py-2 rounded-xl bg-red-50 border border-red-100 text-red-400 hover:text-red-600 hover:border-red-300 transition-all">
                      <FiRefreshCw size={12} /> Refresh
                    </button>
                  </div>

                  {historyLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="w-8 h-8 border-[3px] border-red-100 border-t-red-500 rounded-full animate-spin" />
                    </div>
                  ) : history.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 rounded-3xl bg-red-50 border border-red-100 flex items-center justify-center mb-4">
                        <FiBell size={28} className="text-red-200" />
                      </div>
                      <p className="text-sm font-semibold text-gray-500">No broadcast history yet</p>
                      <p className="text-xs text-gray-400 mt-1">Notifications you send will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pagedHistory.map((item, i) => (
                        <div key={item._id || i} className="hcard flex gap-4 p-4 sm:p-5 rounded-2xl border border-red-50 bg-[#fffafa] hover:border-red-200">
                          {item.image ? (
                            <button onClick={() => setPreviewImg(item.image)} className="flex-shrink-0 group">
                              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-red-100">
                                <img src={item.image} alt="" className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                                  <FiEye size={14} className="text-white" />
                                </div>
                              </div>
                            </button>
                          ) : (
                            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
                              <FiBell size={22} className="text-red-200" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <p className="font-semibold text-sm text-gray-900 leading-snug">{item.title}</p>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                                  {item.recipientCount} users
                                </span>
                                {item.type && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize bg-gray-100 text-gray-500">
                                    {item.type}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{item.message}</p>
                            <div className="flex items-center gap-1.5 mt-2.5 text-[11px] text-gray-400">
                              <FiClock size={10} />
                              {item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "—"}
                            </div>
                          </div>
                          <div className="self-center flex-shrink-0 text-red-200"><FiChevronRight size={16} /></div>
                        </div>
                      ))}
                    </div>

                  )}
                  {historyTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-red-50 mt-4 flex-wrap gap-3">
                      <p className="text-xs text-gray-400">
                        Showing <span className="font-semibold text-gray-600">{(historyPage - 1) * HISTORY_PER_PAGE + 1}–{Math.min(historyPage * HISTORY_PER_PAGE, history.length)}</span> of <span className="font-semibold text-gray-600">{history.length}</span>
                      </p>
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={historyPage === 1}
                          onClick={() => setHistoryPage((p) => p - 1)}
                          className="w-8 h-8 rounded-xl border border-red-100 bg-white flex items-center justify-center text-red-400 hover:bg-red-50 hover:border-red-300 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FiChevronRight size={14} className="rotate-180" />
                        </button>

                        {historyPageNums.map((p, i) =>
                          p === "..." ? (
                            <span key={`d${i}`} className="text-xs text-gray-400 px-1">…</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => setHistoryPage(p)}
                              className={`w-8 h-8 rounded-xl border text-xs font-semibold flex items-center justify-center transition-all ${historyPage === p
                                  ? "bg-gradient-to-br from-red-500 to-red-600 border-transparent text-white shadow-md shadow-red-200"
                                  : "border-red-100 bg-white text-gray-600 hover:bg-red-50 hover:border-red-300"
                                }`}
                            >
                              {p}
                            </button>
                          )
                        )}

                        <button
                          disabled={historyPage === historyTotalPages}
                          onClick={() => setHistoryPage((p) => p + 1)}
                          className="w-8 h-8 rounded-xl border border-red-100 bg-white flex items-center justify-center text-red-400 hover:bg-red-50 hover:border-red-300 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FiChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {previewImg && <ImagePreviewModal src={previewImg} onClose={() => setPreviewImg(null)} />}
    </>
  );
};

export default AdminBroadcast;